'use client'

import { useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import {
  ArrowRight,
  BarChart3,
  Briefcase,
  CheckCircle2,
  ClipboardList,
  Clock,
  History,
  PieChart as PieChartIcon,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Cell, Label, Pie, PieChart } from 'recharts'
import {
  CASE_STATUS_LABEL,
  CASE_STATUS_PIPELINE,
  FlowBadge,
  SERVICE_TYPE_LABEL,
  SERVICE_TYPE_TO_FLOW,
  StatCard,
  type CaseStatus,
  type ServiceType,
} from '@/features/cases'
import { useAdminOverview } from '@/features/admin/hooks/use-admin-overview'
import { formatDate } from '@/lib/format/date'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardDescription, CardTitle } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { CardCanvas, CanvasPanel } from '@/components/patterns/nested-surface'
import { ErrorBoundary } from '@/components/errors/error-boundary'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import { ListSkeleton } from '@/components/patterns/listSkeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminShell } from '@/features/admin/components/admin-shell'

// `echarts` is a large canvas charting library only this page needs — loaded on demand instead of
// statically, so it doesn't ride along in every `/admin/*` route's bundle via the `features/admin`
// barrel. `ssr: false` because ECharts itself requires a DOM canvas.
const AdminStatusChart = dynamic(
  () => import('@/features/admin/components/admin-status-chart').then((m) => m.AdminStatusChart),
  { ssr: false },
)

const STATUS_CHART_SHORT_LABEL: Partial<Record<CaseStatus, string>> = {
  consultation_scheduled: 'Consult. Scheduled',
  consultation_completed: 'Consult. Completed',
}

const leadsChartConfig = {
  count: { label: 'Leads' },
  portfolio_audit: { label: SERVICE_TYPE_LABEL.portfolio_audit, color: 'var(--chart-1)' },
  trades_audit: { label: SERVICE_TYPE_LABEL.trades_audit, color: 'var(--chart-2)' },
  build_portfolio: { label: SERVICE_TYPE_LABEL.build_portfolio, color: 'var(--chart-3)' },
} satisfies ChartConfig

const SERVICE_TYPE_ROW_ICON: Record<ServiceType, typeof Briefcase> = {
  portfolio_audit: Briefcase,
  trades_audit: Clock,
  build_portfolio: Sparkles,
}

const SERVICE_TYPE_CHART_VAR: Record<ServiceType, string> = {
  portfolio_audit: 'var(--chart-1)',
  trades_audit: 'var(--chart-2)',
  build_portfolio: 'var(--chart-3)',
}

export function AdminOverviewView() {
  const router = useRouter()
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType | 'all'>('all')
  const { data, isLoading, isError, error, refetch } = useAdminOverview({
    serviceType: serviceTypeFilter === 'all' ? undefined : serviceTypeFilter,
  })

  const statusChartData = useMemo(() => {
    const counts = data?.casesByStatus ?? []
    return CASE_STATUS_PIPELINE.filter(
      (status) => (counts.find((c) => c.status === status)?.count ?? 0) > 0,
    ).map((status) => ({
      status,
      label: STATUS_CHART_SHORT_LABEL[status] ?? CASE_STATUS_LABEL[status],
      fullLabel: CASE_STATUS_LABEL[status],
      count: counts.find((c) => c.status === status)?.count ?? 0,
    }))
  }, [data?.casesByStatus])
  const statusChartTotal = statusChartData.reduce((s, r) => s + r.count, 0)

  const totalLeads = data?.leadsByType.reduce((s, r) => s + r.count, 0) ?? 0
  const leadsChartData = useMemo(() => {
    return (data?.leadsByType ?? [])
      .filter((row) => row.count > 0)
      .map((row) => ({
        serviceType: row.serviceType,
        count: row.count,
        fill: SERVICE_TYPE_CHART_VAR[row.serviceType],
        share: totalLeads ? (row.count / totalLeads) * 100 : 0,
      }))
  }, [data?.leadsByType, totalLeads])

  if (isError) {
    return (
      <AdminShell
        active="overview"
        title="Overview"
        description="A snapshot of case volume, review load, and recent activity."
      >
        <QueryErrorState error={error} onRetry={() => void refetch()} />
      </AdminShell>
    )
  }

  return (
    <AdminShell
      fitViewport
      active="overview"
      title="Overview"
      description="A snapshot of case volume, review load, and recent activity."
    >
      {isLoading || !data ? (
        <ListSkeleton rows={6} />
      ) : (
        <>
          <div className="@xl/main:grid-cols-2 @4xl/main:grid-cols-4 grid grid-cols-1 gap-4">
            <StatCard
              label="Total Investors"
              value={data.totalInvestors}
              footnote="Onboarded to date"
              icon={<Users />}
              tint="var(--chart-1)"
            />
            <StatCard
              label="Active Cases"
              value={data.activeCases}
              footnote="Not yet published"
              icon={<Briefcase />}
              tint="var(--chart-5)"
            />
            <StatCard
              label="Awaiting Review"
              value={data.awaitingReview}
              trend={data.unassignedCount > 0 ? `${data.unassignedCount} unassigned` : undefined}
              trendUp={false}
              footnote="Submitted or under review"
              icon={<Clock />}
              tint="var(--chart-3)"
            />
            <StatCard
              label="Published"
              value={data.publishedThisMonth}
              trend="This month"
              trendUp
              footnote="Delivered to investors"
              icon={<CheckCircle2 />}
              tint="var(--chart-2)"
            />
          </div>

          <ErrorBoundary label="the overview charts">
            <div className="@xl/main:grid-cols-[1fr_1.4fr] @4xl/main:min-h-80 @4xl/main:flex-1 grid grid-cols-1 gap-4">
              {/* @container/card stays on the outer surface so the inner `@[360px]/card` breakpoints keep
                  measuring roughly the width they were tuned against. */}
              <CardCanvas className="@container/card">
                <CanvasPanel className="flex min-h-0 flex-1 flex-col gap-3 p-4">
                  <div className="flex flex-row items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="border-primary/20 bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-xl border">
                        <PieChartIcon className="size-4" />
                      </span>
                      <div>
                        <CardTitle>Leads by Type</CardTitle>
                        <CardDescription>
                          Which of the services each case came in through.
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto">
                    <div className="@[360px]/card:grid-cols-[auto_1fr] grid grid-cols-1 items-center gap-6">
                      <ChartContainer
                        config={leadsChartConfig}
                        className="@[440px]/card:w-48 relative mx-auto aspect-square max-h-full w-32 max-w-full shrink-0"
                      >
                        <PieChart>
                          <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel nameKey="serviceType" />}
                          />
                          <Pie
                            data={leadsChartData}
                            dataKey="count"
                            nameKey="serviceType"
                            startAngle={90}
                            endAngle={-270}
                            innerRadius="64%"
                            outerRadius="100%"
                            paddingAngle={2}
                            cornerRadius={0}
                          >
                            {leadsChartData.map((row) => (
                              <Cell key={row.serviceType} fill={row.fill} />
                            ))}
                            <Label
                              content={({ viewBox }) => {
                                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                                  return (
                                    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                                      <tspan
                                        x={viewBox.cx}
                                        y={(viewBox.cy || 0) - 6}
                                        className="fill-foreground text-3xl font-bold tabular-nums"
                                      >
                                        {totalLeads}
                                      </tspan>
                                      <tspan
                                        x={viewBox.cx}
                                        y={(viewBox.cy || 0) + 16}
                                        className="fill-muted-foreground text-xs"
                                      >
                                        Total leads
                                      </tspan>
                                    </text>
                                  )
                                }
                              }}
                            />
                          </Pie>
                        </PieChart>
                      </ChartContainer>

                      <div className="flex flex-col">
                        {leadsChartData.map((row, i) => {
                          const Icon = SERVICE_TYPE_ROW_ICON[row.serviceType]
                          return (
                            <div
                              key={row.serviceType}
                              className={`flex items-center gap-3 py-2.5 ${i > 0 ? 'border-t' : ''}`}
                            >
                              <span
                                className="flex size-9 shrink-0 items-center justify-center rounded-lg"
                                style={{
                                  background: `color-mix(in oklch, ${row.fill} 15%, transparent)`,
                                  color: row.fill,
                                }}
                              >
                                <Icon className="size-4" />
                              </span>
                              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <div className="flex items-center justify-between gap-2 text-sm">
                                  <span className="font-medium">
                                    {SERVICE_TYPE_LABEL[row.serviceType]}
                                  </span>
                                  <span
                                    className="font-semibold tabular-nums"
                                    style={{ color: row.fill }}
                                  >
                                    {row.count} ({row.share.toFixed(0)}%)
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="bg-muted h-1.5 flex-1 overflow-hidden rounded-full">
                                    <div
                                      className="h-full rounded-full"
                                      style={{ width: `${row.share}%`, backgroundColor: row.fill }}
                                    />
                                  </div>
                                  <span className="text-muted-foreground text-xs tabular-nums">
                                    {row.share.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </CanvasPanel>

                <CanvasPanel className="flex items-center justify-between gap-3 p-3 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                      <TrendingUp className="size-4" />
                    </span>
                    <div className="flex flex-col leading-tight">
                      <span className="text-muted-foreground">Total leads</span>
                      <span className="font-semibold tabular-nums">{totalLeads}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="group"
                    onClick={() => router.push('/admin/cases')}
                  >
                    View all leads
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </CanvasPanel>
              </CardCanvas>

              <CardCanvas>
                <CanvasPanel className="flex min-h-0 flex-1 flex-col gap-3 p-4">
                  <div className="flex flex-row items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="border-primary/20 bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-xl border">
                        <BarChart3 className="size-4" />
                      </span>
                      <div>
                        <CardTitle>Cases by Status</CardTitle>
                        <CardDescription>
                          Current pipeline distribution across every open case.
                        </CardDescription>
                      </div>
                    </div>
                    <Select
                      value={serviceTypeFilter}
                      onValueChange={(v) => setServiceTypeFilter(v as ServiceType | 'all')}
                    >
                      <SelectTrigger size="sm" className="w-[160px] shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All services</SelectItem>
                        <SelectItem value="portfolio_audit">
                          {SERVICE_TYPE_LABEL.portfolio_audit}
                        </SelectItem>
                        <SelectItem value="trades_audit">
                          {SERVICE_TYPE_LABEL.trades_audit}
                        </SelectItem>
                        <SelectItem value="build_portfolio">
                          {SERVICE_TYPE_LABEL.build_portfolio}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col">
                    <div className="@4xl/main:min-h-44 h-full min-h-72 w-full">
                      <AdminStatusChart data={statusChartData} />
                    </div>
                  </div>
                </CanvasPanel>

                <CanvasPanel className="flex items-center justify-between gap-3 p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="text-muted-foreground size-4" />
                    <span className="font-semibold tabular-nums">{statusChartTotal}</span>
                    <span className="text-muted-foreground">
                      case{statusChartTotal === 1 ? '' : 's'} across {statusChartData.length}{' '}
                      pipeline stage
                      {statusChartData.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="group"
                    onClick={() => router.push('/admin/cases')}
                  >
                    View pipeline
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </CanvasPanel>
              </CardCanvas>
            </div>
          </ErrorBoundary>

          {/* The chart row above can render taller than the space its own flex/grid sizing
              reserves (its content's real height can exceed `min-h-80`), and neither `CardCanvas`
              nor `CanvasPanel` clips overflow — so relying on the parent's `gap-4`/`gap-6` alone
              isn't always enough room to keep this card from visually overlapping it. A generous
              explicit top margin guarantees real separation regardless. */}
          <CardCanvas className="mt-6 md:mt-10">
            <CanvasPanel className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="border-primary/20 bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-xl border">
                    <History className="size-4" />
                  </span>
                  <div>
                    <CardTitle>Recent Cases</CardTitle>
                    <CardDescription>The 10 most recently created cases.</CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="group"
                  onClick={() => router.push('/admin/cases')}
                >
                  View all
                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reference</TableHead>
                      <TableHead>Investor</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Advisor</TableHead>
                      <TableHead className="text-right">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recentCases.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                          No cases yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {data.recentCases.map((c) => (
                      <TableRow
                        key={c.id}
                        className="cursor-pointer"
                        onClick={() => router.push(`/admin/clients/${c.investorId}?case=${c.id}`)}
                      >
                        <TableCell className="text-muted-foreground font-mono text-xs">
                          {c.reference}
                        </TableCell>
                        <TableCell className="font-medium">{c.investorName ?? '—'}</TableCell>
                        <TableCell>
                          <FlowBadge flow={SERVICE_TYPE_TO_FLOW[c.serviceType]} />
                        </TableCell>
                        <TableCell>
                          <Badge variant={c.caseStatus === 'published' ? 'default' : 'secondary'}>
                            {CASE_STATUS_LABEL[c.caseStatus]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {c.advisorName ?? 'Unassigned'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right tabular-nums">
                          {formatDate(c.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CanvasPanel>
          </CardCanvas>
        </>
      )}
    </AdminShell>
  )
}
